/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { FC } from 'react';
import { Paper, Stack, Typography, Grid } from '@mui/material';
import { StoreAttribute, StoreDetails } from '@/data/types/Store';
import { storeLocatorInfoBoxSXMobile } from '@/components/content/StoreLocator/styles/infoBox';
import { useLocalization } from '@/data/Localization';
import HTMLReactParser from 'html-react-parser';
import { infoBoxStackLevelThreeSX } from '@/components/content/StoreLocator/styles/infoBoxStackLevelThree';
import { infoBoxStackLevelTwoSX } from '@/components/content/StoreLocator/styles/infoBoxStackLevelTwo';
import { infoBoxStackLevelOneSX } from '@/components/content/StoreLocator/styles/infoBoxStackLevelOne';
import { stylehowToGet } from '@/components/content/StoreLocator/styles/SideList/titleTypography';
import DirectionsIcon from '@mui/icons-material/Directions';
import PhoneIcon from '@mui/icons-material/Phone';
import { combineSX } from '@/utils/combineSX';

export const StoreLocatorInfoBoxMobile: FC<{ store: StoreDetails }> = ({ store }) => {
	const localization = useLocalization('StoreLocator');
	const storeHours = store?.attributes?.find(
		(attribute: StoreAttribute) => attribute.name === 'StoreHours'
	);

	const getGoogleMapsLink = () => {
		if (!store || !store.coordinates) return '#';
		const { lat, lng } = store.coordinates;
		return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
	};

	return (
		<Paper sx={combineSX([storeLocatorInfoBoxSXMobile, { boxShadow: 'none' }])}>
			<Stack direction="row" spacing={2} sx={infoBoxStackLevelOneSX}>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={infoBoxStackLevelTwoSX}>
					<Stack sx={infoBoxStackLevelThreeSX} spacing={1}>
					<Grid sx={{ display: 'flex', alignItems: 'center' }}>
								<Typography sx={combineSX([stylehowToGet, { marginTop: '2px' }])}>
									<a
										href={`tel:+52${store?.phone}`}
										style={{ textDecoration: 'none', color: 'inherit' }}
									>
										<PhoneIcon sx={{ marginBottom: '-5px' }} />
										{store?.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')}
									</a>
								</Typography>
								<Typography
									data-testid="store-locator-info-box-get-directions"
									id="store-locator-info-box-get-directions"
									onClick={() => window.open(getGoogleMapsLink(), '_blank')}
									sx={combineSX([stylehowToGet, { marginLeft: '25px' }])}
								>
									<DirectionsIcon />
									{localization.getDirections.t()}
								</Typography>
								{storeHours ? (
									<>
										<Typography>{storeHours.displayName + ':'}</Typography>
										<Typography>{HTMLReactParser(storeHours.displayValue)}</Typography>
									</>
								) : null}
							</Grid>
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	);
};
